import { ClaimFormSchema, ClaimFormSchemaProperty } from './types'

/** Get properties for a section of the schema */
export function getSectionProperties(
  schema: ClaimFormSchema,
  sectionKey: string,
): Record<string, ClaimFormSchemaProperty> {
  return schema.properties[sectionKey]?.properties ?? {}
}

/** Check if a field exists in a schema section */
export function hasField(schema: ClaimFormSchema, sectionKey: string, fieldKey: string): boolean {
  return fieldKey in getSectionProperties(schema, sectionKey)
}

/** Get the regex pattern for a field, if any */
export function getFieldPattern(
  schema: ClaimFormSchema,
  sectionKey: string,
  fieldKey: string,
): string | undefined {
  return getSectionProperties(schema, sectionKey)[fieldKey]?.pattern
}

/** Check if a field is required */
export function isFieldRequired(
  schema: ClaimFormSchema,
  sectionKey: string,
  fieldKey: string,
): boolean {
  const required = schema.properties[sectionKey]?.required ?? []
  return required.includes(fieldKey)
}

/** Get the label for a field from schema metadata */
export function getFieldLabel(
  schema: ClaimFormSchema,
  sectionKey: string,
  fieldKey: string,
): string | undefined {
  return getSectionProperties(schema, sectionKey)[fieldKey]?.label
}

/** Check if a required field has a valid value. Returns true if the field doesn't exist, isn't required, or has a non-empty value. Optionally validates against the field's pattern. */
export function isFieldSatisfied(
  schema: ClaimFormSchema,
  sectionKey: string,
  fieldKey: string,
  value: string,
): boolean {
  if (!hasField(schema, sectionKey, fieldKey) || !isFieldRequired(schema, sectionKey, fieldKey)) {
    return true
  }
  if (value === '') {
    return false
  }
  const pattern = getFieldPattern(schema, sectionKey, fieldKey)
  if (pattern) {
    return new RegExp(pattern).test(value)
  }
  return true
}

/** Get enum values for a field */
export function getFieldEnum(
  schema: ClaimFormSchema,
  sectionKey: string,
  fieldKey: string,
): string[] | undefined {
  return getSectionProperties(schema, sectionKey)[fieldKey]?.enum
}

/** Get the maxLength constraint for a field, if any */
export function getFieldMaxLength(
  schema: ClaimFormSchema,
  sectionKey: string,
  fieldKey: string,
): number | undefined {
  return getSectionProperties(schema, sectionKey)[fieldKey]?.maxLength
}

/** Get the format constraint for a field, if any (e.g., 'email') */
export function getFieldFormat(
  schema: ClaimFormSchema,
  sectionKey: string,
  fieldKey: string,
): string | undefined {
  return getSectionProperties(schema, sectionKey)[fieldKey]?.format
}
